import { DropdownButton, Tooltip } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type { Tag } from 'lib-shared-types-api';
import { useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import {
  CategorizedTagsList,
  CategorizedTagsListProps,
} from 'components/categorizedTagsList';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { useCommandHandler } from 'utils/commands';

type TagsDropdownButtonProps = Pick<CategorizedTagsListProps, 'onChange'> & {
  /** The tags. */
  tags: readonly Tag[];
  /** Whether the tags are loading. */
  isLoading: boolean;
  /** Whether the dropdown should be disabled. */
  disabled?: boolean;
};

/** Represents a tags dropdown button. */
export const TagsDropdownButton = ({
  tags,
  disabled,
  isLoading,
  onChange,
}: TagsDropdownButtonProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownButtonRef = useRef<DropdownButton | null>(null);

  useCommandHandler({
    name: 'conversation:tagsSelector',
    actionFn: () => {
      if (!disabled) dropdownButtonRef.current?.handleDropdownToggle();
    },
  });

  useUpdateEffect(() => {
    if (isOpen) {
      ampli.conversationHeaderTagMenuOpened();
    }
  }, [isOpen]);

  const tooltipContent = (
    <CommandKeybindingsTooltipContent commandName="conversation:tagsSelector">
      Add conversation tag
    </CommandKeybindingsTooltipContent>
  );

  return (
    <Tooltip content={tooltipContent} disabled={disabled} wrap="div">
      <DropdownButton
        ref={dropdownButtonRef}
        icon="tag"
        show={isOpen}
        isLoading={isLoading}
        buttonProps={{ variant: 'tertiary', disabled }}
        fixed={true}
        dropdown={
          <CategorizedTagsList
            isActive={isOpen}
            selectedTags={tags}
            onChange={onChange}
          />
        }
        onToggle={setIsOpen}
      >
        Add tag
      </DropdownButton>
    </Tooltip>
  );
};
